import { Icon } from '@phosphor-icons/react'
import React from 'react'
import { z } from 'zod'

export type TextInputSize = 'small' | 'medium' | 'large'

export type TextInputLabelPosition = 'top' | 'left'

// Note: shared visual state for field-like controls (text input, textarea, select).
// Precedence reflects the design — a disabled field always looks disabled, even when
// it also carries an error (the error message is still shown below).
export const FieldStateEnum = z.enum(['disabled', 'readOnly', 'error', 'default'])

export type FieldState = z.infer<typeof FieldStateEnum>

export interface TextInputTrailingAction {
  icon: Icon
  /** Accessible name of the button (it has no visible text). */
  label: string
  onClick: () => void
}

export type TextInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  /** Field label. Rendered above the input by default (`labelPosition`). */
  label?: string
  /** Where the label sits relative to the input. Defaults to `top`. */
  labelPosition?: TextInputLabelPosition
  /** Optional info tooltip rendered as an icon next to the label. */
  tooltip?: string
  /** Helper text shown beneath the field. Hidden while `error` is present. */
  helperText?: string
  /** Error message. Switches the field to the error state and replaces the helper text. */
  error?: string
  /** Leading/trailing icon. */
  icon?: Icon
  /**
   * Custom leading content rendered in the leading slot, in place of `icon`.
   * Sized to match the input's text (not an icon), so it suits compound controls
   * like a currency prefix or a country selector. Takes precedence over `icon`.
   */
  leadingSlot?: React.ReactNode
  /**
   * Soft character limit. When set, a `count/limit` counter is shown.
   * Exceeding it puts the field in the error state (the value is not truncated).
   */
  characterLimit?: number
  /** Shows a clear (X) button while the field has a value and is interactive. */
  clearable?: boolean
  /** When provided, shows a restore button that calls this handler. */
  onRestore?: () => void
  /**
   * Extra icon button on the right of the field, after the clear/restore buttons - e.g. a
   * show/hide toggle for a password. Interactive fields only.
   */
  trailingAction?: TextInputTrailingAction
  size?: TextInputSize
  /**
   * The footer (helper text / error / counter) renders only when `helperText`,
   * `error`, or `characterLimit` is actually set — otherwise no space is reserved.
   * Set this to `true` to always reserve the footer row even when empty, so an
   * appearing error/helper text does not shift the layout.
   */
  withReservedFooter?: boolean
  /**
   * Ref forwarded to the inner `InputWrapper` element (the bordered box).
   * Use this when a consumer needs to measure or anchor something to the
   * field box specifically — e.g., a floating dropdown that should align with
   * the input border, not the outer container that includes the label/footer.
   */
  inputWrapperRef?: React.Ref<HTMLLabelElement>
  /**
   * Overrides the `data-component-id` on the root element (defaults to `"TextInput"`).
   * Lets a thin wrapper (e.g. `DatePickerInput`) identify its root distinctly from a
   * plain `TextInput`, so CSS elsewhere (e.g. a fixed-width filters row) can target one
   * without affecting the other.
   */
  dataComponentId?: string
}
